import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { COLORS, Panel, Table, Button, Field, inputStyle, StatTile, Spinner, Banner, money, date } from '../components/ui';
import { BarChart, GroupedBarChart } from '../components/charts';

/** Programme overview — one card per active PM, plus cost and completion. */
export default function DashboardPage() {
    const today = new Date().toISOString().substring(0, 10);
    const yearAgo = new Date(Date.now() - 365 * 86400000).toISOString().substring(0, 10);

    const [from, setFrom] = useState(yearAgo);
    const [to, setTo] = useState(today);
    const [data, setData] = useState(null);
    const [cost, setCost] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    // 33 of 59 active programmes have no work orders in a 12-month window, so the
    // unfiltered page is mostly empty rows. Default to the ones with activity;
    // the silent ones are a click away and the banner points at them.
    const [view, setView] = useState('active');
    const [q, setQ] = useState('');

    const load = React.useCallback(() => {
        setLoading(true); setError(null);
        Promise.all([api.pmProgrammes({ from, to }), api.pmCost({ from, to })])
            .then(([p, c]) => { setData(p); setCost(c); })
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    }, [from, to]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <Spinner label="Reading MC" />;
    if (error) return <Banner tone="danger">{error}</Banner>;

    const progs = data.programmes;
    const live = progs.filter(p => !p.scheduleDisabled && p.assets > 0);
    const silent = progs.filter(p => p.scheduleDisabled || p.assets === 0);
    const totals = progs.reduce((a, p) => ({
        wos: a.wos + p.wos, overdue: a.overdue + p.overdue,
        completed: a.completed + p.completed, cost: a.cost + p.costActual,
    }), { wos: 0, overdue: 0, completed: 0, cost: 0 });

    // One filter drives the charts and the table, so the page reads as one thing.
    const VIEWS = [
        { key: 'active', label: 'Has work orders', match: p => p.wos > 0 },
        { key: 'overdue', label: 'Has overdue', match: p => p.overdue > 0 },
        { key: 'silent', label: 'Generating nothing', match: p => p.scheduleDisabled || p.assets === 0 },
        { key: 'gldrift', label: 'GL drift', match: p => p.distinctGLs > 1 },
        { key: 'all', label: 'All', match: () => true },
    ];
    const matcher = (VIEWS.find(v => v.key === view) || VIEWS[0]).match;
    const needle = q.trim().toLowerCase();
    const shown = progs.filter(p => matcher(p) &&
        (!needle || `${p.pmid} ${p.name}`.toLowerCase().includes(needle)));

    const withWos = shown.filter(p => p.wos > 0);
    const completionData = withWos
        .slice().sort((a, b) => (b.completionPct || 0) - (a.completionPct || 0)).slice(0, 20)
        .map(p => ({ label: p.pmid, value: p.completionPct || 0, tooltip: `${p.completed} of ${p.wos} work orders closed` }));

    const overdueData = withWos
        .slice().sort((a, b) => b.overdue - a.overdue).slice(0, 12)
        .map(p => ({ label: p.pmid, value: p.overdue, color: COLORS.critical, tooltip: `${p.sites} sites · ${p.assets} assets` }));

    // Group on AccountID — 4540 and 4680 are both "RM - General".
    const byAccount = {};
    for (const r of cost.rows) {
        const k = r.AccountID || '(none)';
        byAccount[k] = byAccount[k] || { label: `${k} ${r.AccountName || ''}`.trim(), actual: 0, estimated: 0 };
        byAccount[k].actual += r.CostActual || 0;
        byAccount[k].estimated += r.CostEstimated || 0;
    }
    const costData = Object.values(byAccount).sort((a, b) => b.actual - a.actual).slice(0, 10);

    return (
        <div>
            <h1 style={{ fontSize: 22, margin: '0 0 4px', color: COLORS.ink }}>PM compliance</h1>
            <p style={{ color: COLORS.ink2, fontSize: 13, marginTop: 0 }}>
                Live from entUNITED. Window {date(data.window.from)} – {date(data.window.to)}.
            </p>

            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 14, flexWrap: 'wrap' }}>
                <Field label="From"><input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle} /></Field>
                <Field label="To"><input type="date" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} /></Field>
                <Button onClick={load}>Apply</Button>
                <Field label="Find a programme">
                    <input value={q} onChange={e => setQ(e.target.value)} placeholder="name or PM id"
                        style={{ ...inputStyle, width: 220 }} />
                </Field>
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 18, flexWrap: 'wrap' }}>
                {VIEWS.map(v => {
                    const n = progs.filter(v.match).length;
                    const on = view === v.key;
                    return (
                        <button key={v.key} onClick={() => setView(v.key)} style={{
                            background: on ? COLORS.link : '#fff',
                            color: on ? '#fff' : COLORS.ink2,
                            border: `1px solid ${on ? COLORS.link : COLORS.axis}`,
                            padding: '5px 12px', borderRadius: 14, fontSize: 12.5,
                            fontWeight: on ? 600 : 400, cursor: 'pointer',
                        }}>{v.label} <span style={{ opacity: 0.75 }}>{n}</span></button>
                    );
                })}
                <span style={{ fontSize: 12, color: COLORS.muted, marginLeft: 4 }}>
                    showing {shown.length} of {progs.length}
                </span>
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
                <StatTile label="Active PMs" value={progs.length} sub={`${live.length} generating`} />
                <StatTile label="Generating nothing" value={silent.length} tone={silent.length ? 'bad' : 'good'}
                    sub="disabled or no assets" />
                <StatTile label="Work orders" value={totals.wos.toLocaleString()} sub="in window" />
                <StatTile label="Overdue" value={totals.overdue.toLocaleString()} tone={totals.overdue ? 'bad' : 'good'} />
                <StatTile label="Completed" value={totals.completed.toLocaleString()} />
                <StatTile label="Actual cost" value={money(totals.cost)} />
            </div>

            {silent.length > 0 && (
                <Banner tone="warn">
                    {silent.length} active PM{silent.length > 1 ? 's' : ''} cannot generate a work order —
                    schedule-disabled or no assets attached. <Link to="/exceptions" style={{ color: 'inherit', fontWeight: 600 }}>See exceptions</Link>.
                </Banner>
            )}

            <Panel title="Cycle completion by programme"
                subtitle={`Closed or complete as a share of work orders raised in the window${completionData.length === 20 ? ' — highest 20' : ''}`}>
                <BarChart data={completionData} format={v => `${v}%`} />
            </Panel>

            <Panel title="Overdue work orders by programme"
                subtitle={`Past target date and still open${overdueData.length === 12 ? ' — worst 12' : ''}`}>
                <BarChart data={overdueData} format={v => v.toLocaleString()} color={COLORS.critical} />
            </Panel>

            <Panel title={`Programmes — ${shown.length}`} subtitle="Click a programme to see its sites">
                <Table
                    rowKey={p => p.pmpk}
                    columns={[
                        { key: 'pmid', label: 'PM', render: p => <Link to={`/review?pmpk=${p.pmpk}`} style={{ color: COLORS.link }}>{p.pmid}</Link> },
                        { key: 'name', label: 'Name', wrap: true },
                        // RepairCenterID is blank on 54 of 59 programmes — it lives on
                        // the work orders, not the PM — so a column here is just noise.
                        { key: 'frequency', label: 'Frequency' },
                        { key: 'sites', label: 'Sites', align: 'right' },
                        { key: 'assets', label: 'Assets', align: 'right' },
                        { key: 'wos', label: 'WOs', align: 'right' },
                        { key: 'issued', label: 'Issued', align: 'right' },
                        { key: 'completed', label: 'Complete', align: 'right' },
                        {
                            key: 'overdue', label: 'Overdue', align: 'right',
                            render: p => <span style={{ color: p.overdue ? COLORS.critical : COLORS.ink, fontWeight: p.overdue ? 600 : 400 }}>{p.overdue}</span>
                        },
                        { key: 'completionPct', label: 'Complete %', align: 'right', render: p => p.completionPct == null ? '' : `${p.completionPct}%` },
                        { key: 'costActual', label: 'Actual', align: 'right', render: p => money(p.costActual) },
                        { key: 'nextScheduled', label: 'Next due', render: p => date(p.nextScheduled) },
                        {
                            key: 'health', label: 'Health', render: p => p.scheduleDisabled
                                ? <span style={{ color: COLORS.critical, fontWeight: 600 }}>disabled</span>
                                : p.assets === 0 ? <span style={{ color: COLORS.critical, fontWeight: 600 }}>no assets</span>
                                    : p.manualGeneration ? <span style={{ color: '#8a6100' }}>manual</span>
                                        : p.distinctGLs > 1 ? <span style={{ color: '#8a6100' }}>GL drift</span> : 'ok'
                        },
                    ]}
                    rows={shown}
                    empty={q ? `No programme matches "${q}"` : 'Nothing in this view'}
                    maxHeight={700}
                />
            </Panel>
        </div>
    );
}
